import { type JSX, useState } from 'react';

import type { Tile } from '@schema/tiles.js';

export interface TileGridViewProps {
  columns: number;
  rows: number;
  tiles: Tile[];
  /** Replace the tiles array — typically wired to store.setTiles. */
  onTilesChange: (next: Tile[]) => void;
  /** Called when the user clicks a tile cell; used by the parent to switch to list view + scroll. */
  onTileFocus?: (id: string) => void;
}

/**
 * Visual grid editor — renders the active profile's tile array as a real
 * columns×rows CSS grid. Each cell is either:
 *
 *   • a populated tile slot (idx < tiles.length): shows the tile's icon
 *     and label, is draggable, clickable to focus in the list view
 *   • a deliberate spacer (tile.type === 'spacer'): rendered as a faint
 *     hatched cell that's draggable like any other tile
 *   • an "implicit empty" (idx ≥ tiles.length): plus-cell drop target;
 *     click to insert a new app tile at that position
 *
 * Drop semantics:
 *   - Drop on another cell → swap the two cells
 *   - Drop on an empty cell beyond the array end → array is padded with
 *     spacers up to that index, then the dragged tile is placed
 *
 * The component owns no schema state; it produces a fresh tiles array
 * and hands it to `onTilesChange`, which the parent routes through
 * store.setTiles (one undo entry per drop).
 */
export function TileGridView({
  columns,
  rows,
  tiles,
  onTilesChange,
  onTileFocus,
}: TileGridViewProps): JSX.Element {
  const capacity = columns * rows;
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  /**
   * Take the current dense tiles array and a (from, to) swap, return the
   * new dense tiles array. Trailing implicit-empties are trimmed; middle
   * implicit-empties get materialised as spacer tiles so the array can
   * carry positional meaning.
   */
  function swap(fromIdx: number, toIdx: number): Tile[] {
    if (fromIdx === toIdx) return tiles;
    // Build a working buffer of length max(capacity, fromIdx+1, toIdx+1)
    const buf: (Tile | null)[] = new Array(Math.max(tiles.length, toIdx + 1)).fill(null);
    for (let i = 0; i < tiles.length; i++) buf[i] = tiles[i]!;
    // Swap two slots; either side may be null (implicit empty).
    const a = buf[fromIdx] ?? null;
    const b = buf[toIdx] ?? null;
    buf[fromIdx] = b;
    buf[toIdx] = a;
    // Trim trailing nulls — they're outside the array, not spacers.
    while (buf.length > 0 && buf[buf.length - 1] === null) buf.pop();
    // Remaining nulls in the interior become spacers (positions matter).
    return buf.map((slot) =>
      slot === null
        ? ({ type: 'spacer', id: crypto.randomUUID(), label: 'spacer' } as Tile)
        : slot,
    );
  }

  function handleDrop(targetIdx: number): void {
    if (draggingIdx === null) return;
    if (targetIdx === draggingIdx) {
      setDraggingIdx(null);
      setHoverIdx(null);
      return;
    }
    onTilesChange(swap(draggingIdx, targetIdx));
    setDraggingIdx(null);
    setHoverIdx(null);
  }

  function handleAddAt(idx: number): void {
    // Materialize an App tile at `idx`. Pad up to `idx` with spacers if
    // we're adding past the current array end.
    const buf: Tile[] = tiles.slice();
    while (buf.length < idx) {
      buf.push({ type: 'spacer', id: crypto.randomUUID(), label: 'spacer' });
    }
    buf[idx] = {
      type: 'app',
      id: crypto.randomUUID(),
      label: 'New app',
      packageName: 'com.example.app',
    };
    onTilesChange(buf);
  }

  return (
    <div
      className="grid-view"
      role="grid"
      aria-label="Tile grid"
      aria-rowcount={rows}
      aria-colcount={columns}
      style={{
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
      }}
    >
      {Array.from({ length: capacity }, (_, idx) => {
        const tile = tiles[idx] ?? null;
        const isEmpty = tile === null;
        const isDragging = draggingIdx === idx;
        const isHover = hoverIdx === idx && draggingIdx !== null && draggingIdx !== idx;
        return (
          <Cell
            key={tile?.id ?? `empty-${idx}`}
            idx={idx}
            tile={tile}
            isEmpty={isEmpty}
            isDragging={isDragging}
            isHover={isHover}
            onDragStart={() => setDraggingIdx(idx)}
            onDragEnd={() => {
              setDraggingIdx(null);
              setHoverIdx(null);
            }}
            onDragOver={() => setHoverIdx(idx)}
            onDragLeave={() => {
              if (hoverIdx === idx) setHoverIdx(null);
            }}
            onDrop={() => handleDrop(idx)}
            onAdd={() => handleAddAt(idx)}
            onFocus={(id) => onTileFocus?.(id)}
          />
        );
      })}
    </div>
  );
}

function Cell({
  idx,
  tile,
  isEmpty,
  isDragging,
  isHover,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onAdd,
  onFocus,
}: {
  idx: number;
  tile: Tile | null;
  isEmpty: boolean;
  isDragging: boolean;
  isHover: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onAdd: () => void;
  onFocus: (id: string) => void;
}): JSX.Element {
  if (isEmpty) {
    return (
      <button
        type="button"
        className={`grid-view__cell grid-view__cell--empty${isHover ? ' grid-view__cell--hover' : ''}`}
        onClick={onAdd}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          onDragOver();
        }}
        onDragLeave={onDragLeave}
        onDrop={(e) => {
          e.preventDefault();
          onDrop();
        }}
        aria-label={`Empty cell ${idx + 1} — click to add a tile`}
        data-testid={`grid-cell-${idx}`}
      >
        <span className="grid-view__plus" aria-hidden>
          +
        </span>
      </button>
    );
  }
  if (tile === null) return <></>; // narrowing — TS doesn't quite see it without
  const isSpacer = tile.type === 'spacer';
  return (
    <div
      className={`grid-view__cell${isSpacer ? ' grid-view__cell--spacer' : ''}${
        isDragging ? ' grid-view__cell--dragging' : ''
      }${isHover ? ' grid-view__cell--hover' : ''}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', tile.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver();
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      onClick={() => onFocus(tile.id)}
      data-testid={`grid-cell-${idx}`}
      role="gridcell"
      title={tile.label}
    >
      <TilePreview tile={tile} />
    </div>
  );
}

/**
 * Compact visual of a tile — icon (uploaded data URL, asset ref, or
 * generated letter avatar) plus label. Mirrors the runtime renderer's
 * resolution order so the grid view looks like what ships on-device.
 */
function TilePreview({ tile }: { tile: Tile }): JSX.Element {
  if (tile.type === 'spacer') {
    return <span className="grid-view__spacer" aria-hidden>spacer</span>;
  }
  if (tile.type === 'divider') {
    return (
      <span
        className="grid-view__divider"
        aria-hidden
        style={{ background: tile.color ?? 'var(--ml-accent, #03dac6)', height: `${String(tile.height ?? 8)}px` }}
      />
    );
  }
  return (
    <>
      <span className="grid-view__icon" aria-hidden>
        {tile.iconDataUrl !== undefined ? (
          <img className="grid-view__icon-img" src={tile.iconDataUrl} alt="" />
        ) : (
          <LetterAvatar label={tile.label} />
        )}
      </span>
      {tile.showLabel !== false && (
        <span className="grid-view__label">{tile.label}</span>
      )}
    </>
  );
}

function LetterAvatar({ label }: { label: string }): JSX.Element {
  // Same palette + hash as the renderer's GeneratedIcon, so the preview
  // matches what ships on-device.
  const palette = [
    '#1976d2', '#03a9b3', '#7c4dff', '#e85d75', '#f57c00', '#43a047',
    '#5e35b1', '#0288d1', '#d81b60', '#fb8c00', '#00897b', '#3949ab',
  ];
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) | 0;
  const color = palette[Math.abs(hash) % palette.length] ?? palette[0]!;
  const initial = (label.trim().charAt(0) || '?').toUpperCase();
  return (
    <span className="grid-view__letter" style={{ background: color }}>
      {initial}
    </span>
  );
}
